import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FEATURE_KEYS, isFeatureEnabled } from './redux/featureSlice';
import i18n from './i18n';
import { fetchMasterLocale, hydrateMasterLocaleFromCache, getLanguages } from './redux/translationSlice';
import { fetchFeatureFlags } from './redux/featureSlice';
import AppLayout from './components/layout/AppLayout';
import Login from './pages/Login';
import PrivateRoute from './components/PrivateRoute.jsx';
import Appointment from './pages/Appointment';
import AppointmentList from './pages/AppointmentList';
import Doctor from './pages/Doctor';
import DoctorProfile from './pages/profile/DoctorProfile';
import MyDoctor from './pages/MyDoctor';
import Patient from './pages/Patient';
import MyPatient from './pages/MyPatient';
import PatientLibrary from './pages/library/PatientLibrary.jsx';
import DoctorLibrary from './pages/library/DoctorLibrary';
import Payment from './pages/Payments.jsx';
import PatientProfile from './pages/profile/PatientProfile';
import NotFound from './components/NotFound.jsx';
import NetworkErrorPage from './components/NetworkErrorPage.jsx';
import ServerErrorPage from './components/ServerErrorPage.jsx';
// import RestrictedAccess from './components/RestrictedAccess.jsx';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PatientAppointment from './pages/PatientAppointment.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';
import ActivateAccount from './pages/ActivateAccount.jsx';
import './i18n';
import MyAppointment from './pages/MyAppointment.jsx';
import AdminProfile from './pages/profile/AdminProfile.jsx';
import DoctorDashboard from './pages/Dashboard/DoctorDashboard.jsx';
import PatientDashboard from './pages/Dashboard/PatientDashboard.jsx';
import ReportsDoctors from './pages/reports/ReportsDoctors.jsx';
import ReportsPatients from './pages/reports/ReportsPatients.jsx';
import ReportsCount from './pages/reports/ReportsCount.jsx';
import ReportsDoctorStatus from './pages/reports/ReportsDoctorStatus.jsx';
import AdminDashboard from './pages/Dashboard/AdminDashboard.jsx';
import { ErrorProvider, useError, ERROR_TYPES } from './contexts/ErrorContext.jsx';
import { setGlobalErrorHandler } from './hooks/useAxiosAuth.js';
import { SupportSocketProvider } from './contexts/SupportSocketContext.jsx';
import { isSessionMismatch, forceLogout } from './utils/sessionGuard.js';
// import ChatPage from './pages/ChatPage.jsx';
import PatientChatPage from './pages/chat/PatientChatPage.jsx';
import DoctorChatPage from './pages/chat/DoctorChatPage.jsx';
import Support from './pages/support/Support.jsx';
import SupportChatPage from './pages/support/SupportChatPage.jsx';
import Home from './pages/homePage/Home.jsx';
import DoctorRegister from './pages/homePage/DoctorRegister.jsx';
import PatientRegister from './pages/homePage/PatientRegister.jsx';
import RegisterChoice from './pages/homePage/RegisterChoice.jsx';
import Speciality from './pages/master/Speciality.jsx';
import MasterSupport from './pages/master/Support.jsx';
import Translation from './pages/master/Translation.jsx';
import Country from './pages/master/Country.jsx';
import Profession from './pages/master/Profession.jsx';
import SpecialityCategory from './pages/master/SpecialityCategory.jsx';
import ClickToGo from './pages/ClickToGo.jsx';
import Subscription from './pages/Subscription.jsx';
import SubItems from './pages/SubItems.jsx';
import MySubscription from './pages/MySubscription.jsx';
import SubscriptionPayments from './pages/SubscriptionPayments.jsx';
import AuditLogs from './pages/audit/AuditLogs.jsx';
import AiLog from './pages/audit/AiLog.jsx';
import ContactRequests from './pages/ContactRequests.jsx';
import FeatureGuard from './components/FeatureGuard.jsx';
import RoleRoute from './components/RoleRoute.jsx';
import AIReports from './pages/AIReports.jsx';
import SuperAdminRoute from './components/SuperAdminRoute.jsx';
import Admin from './pages/Admin.jsx';
import Settings from './pages/Settings.jsx';
import Medicines from './pages/Medicines.jsx';
import DoctorSettingsPage from './pages/DoctorSettingsPage.jsx';

// Guard the public sign-up routes against direct URL access when the admin
// has turned the relevant flag off. We wait for the flag fetch to settle
// before redirecting so a fresh page load doesn't bounce out on the default
// 'inactive' fallback before flags resolve.
function RegistrationRouteGuard({ flagKey, children }) {
  const enabled = useSelector((s) => isFeatureEnabled(s, flagKey));
  const featureLoaded = useSelector((s) => s.feature?.loaded);
  if (featureLoaded && !enabled) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppContent() {
  const { errorType, setNetworkError, setServerError, clearError } = useError();
  const dispatch = useDispatch();

  // Set up global error handler for axios
  useEffect(() => {
    setGlobalErrorHandler({ setNetworkError, setServerError, clearError });
  }, [setNetworkError, setServerError, clearError]);

  // Master-data translation bootstrap.
  // 1. Fetch the language master once so the header language popup has the dynamic list.
  // 2. Hydrate the active locale synchronously from localStorage so first render uses
  //    cached labels (stale-while-revalidate — instant render, no flash).
  // 3. Fire a background fetch to refresh the cache.
  // 4. On every i18n language change, hydrate + refetch for the new language.
  // 5. On login (fires a `login` window event), re-run the fetch — logout calls
  //    localStorage.clear() which wipes the master-locale cache, so a fresh fetch
  //    is required once the user is authenticated again.
  useEffect(() => {
    const currentLcode = () =>
      i18n.language || localStorage.getItem('i18nextLng') || 'en';

    const loadLocale = (lcode) => {
      if (!lcode) return;
      dispatch(hydrateMasterLocaleFromCache(lcode));
      dispatch(fetchMasterLocale({ lcode }));
    };

    const refresh = () => {
      dispatch(getLanguages({ page: 1, limit: 100, search: '' }));
      loadLocale(currentLcode());
    };

    // Initial load on app mount.
    refresh();
    // Public pages (Home, Login, RegisterChoice) gate sign-up CTAs on the
    // auth.*Registration flags, so flags must load even before login. The
    // /feature/by-keys route is unauthenticated and the post-login layout
    // skips re-fetching when state.feature.loaded is already true.
    dispatch(fetchFeatureFlags());

    const handleLangChange = (lng) => loadLocale(lng);
    const handleLogin = () => refresh();
    const handleLogout = () => {
      // Re-seed with the current language so public pages (/login, /home) still
      // render translated labels after the logout wipe.
      loadLocale(currentLcode());
    };

    i18n.on('languageChanged', handleLangChange);
    window.addEventListener('login', handleLogin);
    window.addEventListener('logout', handleLogout);
    return () => {
      i18n.off('languageChanged', handleLangChange);
      window.removeEventListener('login', handleLogin);
      window.removeEventListener('logout', handleLogout);
    };
  }, [dispatch]);

  // Cross-tab session guard. Catches the case where another tab in the same
  // browser logs in or out as a different user — if it does, this tab's
  // sessionStorage id no longer matches localStorage's activeSessionId and
  // we exile this tab to /login. forceLogout is idempotent, so we don't need
  // a latch — repeated triggers from focus/storage events while we're already
  // on /login are no-ops.
  useEffect(() => {
    const checkSession = () => {
      if (isSessionMismatch()) forceLogout();
    };

    const onStorage = (e) => {
      // Only react to keys that signal an auth change.
      if (
        e.key === 'activeSessionId' ||
        e.key === 'accessToken' ||
        e.key === 'ID' ||
        e.key === null /* localStorage.clear() — key is null */
      ) {
        checkSession();
      }
    };
    const onFocus = () => checkSession();

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  // Get role from localStorage
  const userRole = localStorage.getItem('Role');
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

  // Decide which dashboard to show as index
  let DefaultDashboard;
  if (isAuthenticated && userRole) {
    if (userRole === 'doctor') {
      DefaultDashboard = <DoctorDashboard />;
    } else if (userRole === 'patient') {
      DefaultDashboard = <PatientDashboard />;
    } else if (userRole === 'admin') {
      DefaultDashboard = <AdminDashboard />;
    } else {
      DefaultDashboard = <Home />;
    }
  } else {
    DefaultDashboard = <Home />;
  }

  // Show error pages when network or server errors occur
  if (errorType === ERROR_TYPES.NETWORK) {
    return <NetworkErrorPage />;
  }

  if (errorType === ERROR_TYPES.SERVER) {
    return <ServerErrorPage />;
  }

  return (
    <Router data-testid="app-router">
      <Routes data-testid="app-routes">
        {/* Public Routes - Always Accessible */}
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register-choice" element={<RegisterChoice />} />
        <Route
          path="/doctor-register"
          element={
            <RegistrationRouteGuard flagKey={FEATURE_KEYS.authDoctorRegistration}>
              <DoctorRegister />
            </RegistrationRouteGuard>
          }
        />
        <Route
          path="/patient-register"
          element={
            <RegistrationRouteGuard flagKey={FEATURE_KEYS.authPatientRegistration}>
              <PatientRegister />
            </RegistrationRouteGuard>
          }
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/activate" element={<ActivateAccount />} />
        {/* Protected Routes - Require Authentication */}
        <Route element={<PrivateRoute />}>
          <Route element={<AppLayout />}>
            {/* Default Dashboard Route */}
            <Route path="/" element={DefaultDashboard} />

            {/* ---- Shared (all roles) ----
                doctorProfile: admin manages a doctor / doctor's own profile / patient views their doctor.
                patientProfile: admin + doctor view a patient's record / patient's own profile. */}
            <Route path="doctorProfile" element={<DoctorProfile />} />
            <Route path="patientProfile" element={<PatientProfile />} />
            <Route path="support" element={<Support />} />
            <Route path="supportchat" element={<SupportChatPage />} />
            {/* <Route path="chat" element={<ChatPage />} /> */}
            {/* <Route path="patientSummaryAI" element={<PatientSummaryAI />} /> */}

            {/* ---- Admin + Doctor ---- (admin uses the doctor dashboard as "view as a doctor") */}
            <Route element={<RoleRoute allow={['admin', 'doctor', 'super_admin']} />}>
              <Route path="aiReports" element={<AIReports />} />
            </Route>
            <Route element={<RoleRoute allow={['admin', 'doctor']} />}>
              <Route path="doctorDashboard" element={<DoctorDashboard />} />
              <Route path="appointment" element={<Appointment />} />
              <Route path="appointmentList" element={<AppointmentList />} />
            </Route>

            {/* ---- Doctor + Patient ---- */}
            <Route element={<RoleRoute allow={['doctor', 'patient']} />}>
              {/* Plan-gated: a doctor whose plan lacks the feature is blocked (patients pass). */}
              <Route element={<FeatureGuard control="online_payment" />}>
                <Route path="payment" element={<Payment />} />
              </Route>
            </Route>

            {/* ---- Doctor only ---- */}
            <Route element={<RoleRoute allow={['doctor']} />}>
              <Route path="myPatient" element={<MyPatient />} />
              {/* Plan-gated: a doctor whose plan lacks the feature is blocked. */}
              <Route element={<FeatureGuard control="patient_library" />}>
                <Route path="patientLibrary" element={<PatientLibrary />} />
              </Route>
              <Route element={<FeatureGuard control="doctor_patient_chat" />}>
                <Route path="patientchat" element={<PatientChatPage />} />
              </Route>
              <Route path="mySubscription" element={<MySubscription />} />
              {/* Doctor's medicines / products master list — gated by the
                  super-admin Medicines feature flag (redirects when off). */}
              <Route path="medicines" element={<RegistrationRouteGuard flagKey={FEATURE_KEYS.medicines}><Medicines /></RegistrationRouteGuard>} />
              {/* Doctor's personal settings (booking rules etc.) */}
              <Route path="doctorSettings" element={<DoctorSettingsPage />} />
            </Route>

            {/* ---- Patient only ---- */}
            <Route element={<RoleRoute allow={['patient']} />}>
              <Route path="patientDashboard" element={<PatientDashboard />} />
              <Route path="patientAppointment" element={<PatientAppointment />} />
              <Route path="myAppointment" element={<MyAppointment />} />
              <Route path="myDoctor" element={<MyDoctor />} />
              <Route path="doctorLibrary" element={<DoctorLibrary />} />
              <Route path="doctorchat" element={<DoctorChatPage />} />
            </Route>

            {/* ---- Admin only ---- */}
            <Route element={<RoleRoute allow={['admin']} />}>
              <Route path="adminDashboard" element={<AdminDashboard />} />
              <Route path="adminProfile" element={<AdminProfile />} />

              {/* Doctors / Patients management */}
              <Route path="doctor" element={<Doctor />} />
              <Route path="patient" element={<Patient />} />

              {/* Reports Section */}
              <Route path="doctors" element={<ReportsDoctors />} />
              <Route path="patients" element={<ReportsPatients />} />
              <Route path="count" element={<ReportsCount />} />
              <Route path="doctor-status" element={<ReportsDoctorStatus />} />

              {/* Master Section (Translation is super-admin only — see below) */}
              <Route path="speciality-category" element={<SpecialityCategory />} />
              <Route path="speciality" element={<Speciality />} />
              <Route path="master-support" element={<MasterSupport />} />
              <Route path="country" element={<Country />} />
              <Route path="profession" element={<Profession />} />

              {/* Subscription */}
              <Route path="subscription" element={<Subscription />} />
              <Route path="subscriptionPayments" element={<SubscriptionPayments />} />
              <Route path="contactRequests" element={<ContactRequests />} />
              {/* Invite Section */}
              <Route path="clicktogo" element={<ClickToGo />} />
              {/* Admin Settings (feature flags) */}
              <Route path="settings" element={<Settings />} />

              {/* ---- Super-admin only ---- (hidden from plain admins)
                   Admins management + Subscription Sub-items + Audit & Security. */}
              <Route element={<SuperAdminRoute />}>
                <Route path="admins" element={<Admin />} />
                <Route path="manageAdminProfile" element={<AdminProfile />} />
                <Route path="translation" element={<Translation />} />
                <Route path="sub-items" element={<SubItems />} />
                <Route path="audit/api" element={<AuditLogs category="api" title="API Logs" description="Every API request — who, what, where, result, duration." />} />
                <Route path="audit/socket" element={<AuditLogs category="socket" title="Socket Logs" description="Real-time Socket.IO events — connections, chat, support, dashboard." />} />
                <Route path="audit/login" element={<AuditLogs category="login" title="Login Logs" description="Authentication events — login, logout, failed attempts." />} />
                <Route path="audit/activity" element={<AuditLogs category="activity" title="Activity Logs" description="Data changes — create / update / delete on records." />} />
                <Route path="audit/appointments" element={<AuditLogs category="appointment" title="Appointment Logs" description="Appointment lifecycle — booked, rescheduled, cancelled, completed, no-show." />} />
                <Route path="audit/security" element={<AuditLogs category="security" title="Security Monitoring" description="Security events — suspicious activity, blocked access." />} />
                <Route path="audit/ai" element={<AiLog />} />
              </Route>
            </Route>
          </Route>
        </Route>

        {/* 404 Not Found */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Router>
  );
}

function App() {
  return (
    <ErrorProvider>
      <SupportSocketProvider>
        <AppContent />
      </SupportSocketProvider>
    </ErrorProvider>
  );
}

export default App;
